import {OFFLINE_STORES,getOfflineStorageDriver} from './offlineStorageAdapter.js';
import {visitasListScope} from './visitasOfflineList.js';
import {isNativePlatform} from '../mobile/capacitorPlatform.js';
import {cloneVisitaSnapshot} from './visitaLocalFirst.js';
import {formRevision,formUpdateCompatibility} from './visitaFormUpdatePolicy.js';

const STORE=OFFLINE_STORES.CONTEXTS,MAX_AGE=7*86400000,MAX_BYTES=16*1024*1024;
const clone=value=>JSON.parse(JSON.stringify(value));
const failure=(code,message)=>Object.assign(new Error(message),{code});
const jobs=new Map();

export function visitaOfflineContextScope(user){
    if(user?.tipo_acceso!=='empresa'||!['access.visitas','visitas.view_any','visitas.editar_formularios','visitas.descargar_formularios_offline'].every(p=>user.permissions?.includes(p)))return null;
    return visitasListScope({...user,roles:(user.roles||[]).map(r=>({...r,id:Number(r.id),hierarchy_level:r.hierarchy_level==null?null:Number(r.hierarchy_level)}))});
}
export function visitaOfflineContextKey(user,visitaId,scope=visitaOfflineContextScope(user)){
    let hash=2166136261;for(const c of scope||'')hash=Math.imul(hash^c.charCodeAt(0),16777619);
    return `context:${Number(user.empresa_id)}:${Number(user.id)}:${Number(visitaId)}:${(hash>>>0).toString(16)}`;
}

export function createVisitaOfflineContext({user,sessionProvider,fetchContext,driver=getOfflineStorageDriver(),isCurrent=()=>true,isOnline=()=>navigator.onLine,now=Date.now,migrate=async()=>{},requiresSqlite=isNativePlatform(),canApply=async()=>true,onApplied=()=>true,withLocalLock=(id,task)=>task()}){
    const scope=visitaOfflineContextScope(user),userId=Number(user?.id),empresaId=Number(user?.empresa_id);
    let disposed=false;
    const client=Symbol();
    const active=async()=>{
        if(disposed||!scope||!isCurrent())throw failure('canceled','El formulario no está habilitado para esta sesión.');
        const session=await sessionProvider();
        if(visitaOfflineContextScope(session?.user)!==scope||!(Number(session.offline_expires_at)>now()))throw failure('forbidden','La autorización local del formulario venció o cambió.');
        return session;
    };
    const storage=async()=>{await active();await driver.init();if(driver.effectiveEngine==='memory'||requiresSqlite&&driver.effectiveEngine!=='sqlite')throw failure('storage','No se pudo abrir el almacenamiento del formulario.');return driver;};
    const validate=(context,id)=>{
        const auth=context?.offline_authorization;
        if(!auth||auth.schema_version!==1||visitaOfflineContextScope(auth.actor)!==scope||Number(auth.visita_id)!==Number(id)
            ||Number(context?.visita?.id)!==Number(id)||!Number.isSafeInteger(Number(auth.sucursal_id))||Number(auth.sucursal_id)<1
            ||Number(context.visita.sucursal_id)!==Number(auth.sucursal_id)||Number(context.context?.sucursal?.id)!==Number(auth.sucursal_id)
            ||!Array.isArray(context.template?.fields)||!/^[a-f0-9]{64}$/.test(auth.template_version||'')
            ||auth.form_revision!==undefined&&!/^[a-f0-9]{64}$/.test(auth.form_revision||'')
            ||!Number.isFinite(auth.authorized_at)||!Number.isFinite(auth.expires_at)||auth.authorized_at>now()+300000
            ||auth.expires_at<=now()||auth.expires_at-auth.authorized_at>MAX_AGE+1000)throw failure('invalid_response','El servidor no confirmó la preparación del formulario.');
        return auth;
    };
    const rowValid=(row,id)=>{
        if(row?.kind!=='context'||row.userId!==userId||row.empresaId!==empresaId||row.visitaId!==Number(id)||row.scope!==scope)return false;
        try{const auth=validate(row.context,id);return row.templateVersion===auth.template_version;}catch{return false;}
    };
    const publishRow=async(row,expectedVersion)=>{
        if(driver.publishVisitaContext)return driver.publishVisitaContext(row,expectedVersion);
        const saved=await driver.mutate(STORE,row.id,previous=>(previous?.version||'')===expectedVersion?row:null);
        if(saved?.version!==row.version)throw failure('changed','El formulario local cambió durante la descarga.');
    };
    const read=id=>withLocalLock(Number(id),async()=>{
        await storage();const key=visitaOfflineContextKey(user,id,scope);
        let row=await driver.get(STORE,key);
        const staged=await driver.get(STORE,key+':candidate');
        // La candidata es el journal: si el proceso murió antes de retirarla,
        // una publicación sobre la copia anterior todavía no está confirmada.
        if(staged?.kind==='candidate'&&staged.scope===scope&&staged.baseVersion&&row?.scope===scope
            &&row.version!==staged.baseVersion&&formRevision(row.context)===formRevision(staged.context)){
            const previous=await driver.get(STORE,key+':previous');
            if(previous?.scope===scope&&previous.userId===userId&&previous.empresaId===empresaId&&previous.version===staged.baseVersion){
                const restored={...previous,id:key,kind:'context',version:crypto.randomUUID()};
                await publishRow(restored,row.version);
                await publishRow({...staged,baseVersion:restored.version,version:crypto.randomUUID()},staged.version);
                row=restored;
            }
        }
        if(!staged){const hint=await driver.get(STORE,key+':hint');if(hint?.scope===scope&&hint.remote_revision===formRevision(row?.context))await driver.compareAndDelete(STORE,hint.id,value=>value?.version===hint.version);}
        await active();return rowValid(row,id)?clone(row.context):null;
    });
    const revoke=async id=>{await storage();for(const suffix of ['',':candidate',':hint'])await driver.compareAndDelete(STORE,visitaOfflineContextKey(user,id,scope)+suffix,row=>row?.scope===scope&&row.userId===userId&&row.empresaId===empresaId);};
    const clearAppliedHint=async(id,revision)=>{const key=visitaOfflineContextKey(user,id,scope)+':hint',hint=await driver.get(STORE,key);if(hint?.scope===scope&&hint.remote_revision===revision)await driver.compareAndDelete(STORE,key,row=>row?.version===hint.version);};
    const publish=async(id,context,expectedVersion,{returnRow=false}={})=>{
        const auth=validate(context,id),key=visitaOfflineContextKey(user,id,scope);
        const current=await driver.get(STORE,key);
        const firma=current?.context?.firma_cierre;
        const next=clone(context);
        if(rowValid(current,id)&&current.templateVersion===auth.template_version&&Number(firma?.user_id)===userId)next.firma_cierre=clone(firma);
        else delete next.firma_cierre;
        const row={id:key,kind:'context',userId,empresaId,visitaId:Number(id),scope,version:crypto.randomUUID(),templateVersion:auth.template_version,context:next,savedAt:now()};
        if(JSON.stringify(row).length*2>MAX_BYTES)throw failure('capacity','El formulario supera el espacio reservado para su preparación.');
        await active();
        await publishRow(row,expectedVersion);
        const confirmed=await driver.get(STORE,key);
        if(confirmed?.version!==row.version||JSON.stringify(confirmed.context)!==JSON.stringify(next))
            throw failure('storage','No se confirmó el contenido completo del formulario guardado.');
        return returnRow?row:clone(next);
    };
    const candidate=async id=>{
        await storage();const row=await driver.get(STORE,visitaOfflineContextKey(user,id,scope)+':candidate');await active();
        return row?.kind==='candidate'&&rowValid({...row,kind:'context'},id)?row:null;
    };
    const promote=id=>withLocalLock(Number(id),async()=>{
        await storage();const staged=await candidate(id);if(!staged)return null;
        const key=visitaOfflineContextKey(user,id,scope),previous=await driver.get(STORE,key);
        const pending=reason=>({context:rowValid(previous,id)?clone(previous.context):null,source:'saved',reason:'form_update_pending',pendingReason:reason});
        if((previous?.version||'')!==staged.baseVersion)return pending('stale');
        if(Number(previous?.context?.visita?.responses_version||0)>Number(staged.context.visita.responses_version||0))return pending('stale');
        const compatible=formUpdateCompatibility(previous?.context,staged.context);
        if(!compatible.safe)return pending(compatible.reason);
        const heldReason=compatible.preserved?'pending_work':'structure_changed';
        if(!await canApply(Number(id)))return pending(heldReason);
        await active();
        if(previous){
            const backup={...previous,id:key+':previous',kind:'previous'};
            await driver.put(STORE,backup);
            if((await driver.get(STORE,backup.id))?.version!==previous.version)throw failure('storage','No se confirmó la copia anterior del formulario.');
        }
        const published=await publish(id,staged.context,previous?.version||'',{returnRow:true});
        let retired=false,rolledBack=false;
        const rollback=async()=>{
            if(rolledBack)return;
            try{
                if(retired&&!await driver.get(STORE,staged.id))await publishRow({...staged,version:crypto.randomUUID()},'');
            }finally{
                let baseVersion='';
                if(previous){
                    const restored={...previous,version:crypto.randomUUID()};
                    await publishRow(restored,published.version);
                    baseVersion=restored.version;
                }else await driver.compareAndDelete(STORE,key,row=>row?.version===published.version);
                const journal=await driver.get(STORE,staged.id);
                if(journal?.baseVersion===staged.baseVersion&&formRevision(journal.context)===formRevision(staged.context))
                    await publishRow({...journal,baseVersion,version:crypto.randomUUID()},journal.version);
                rolledBack=true;
            }
        };
        try{
            await active();
            if(!await canApply(Number(id))){await rollback();return pending(heldReason);}
            retired=await driver.compareAndDelete(STORE,staged.id,row=>row?.version===staged.version);
            if(!retired){await rollback();return pending('stale');}
            await clearAppliedHint(id,formRevision(published.context));
            await active();
            if(!await canApply(Number(id))){await rollback();return pending(heldReason);}
            if(onApplied(Number(id),clone(published.context),previous?.context)===false){await rollback();return pending(heldReason);}
        }catch(error){await rollback();throw error;}
        return {context:clone(published.context),source:'server',updated:true};
    });
    const stage=async(id,context,expectedVersion)=>{
        validate(context,id);await active();
        const key=visitaOfflineContextKey(user,id,scope),current=await driver.get(STORE,key);
        if((current?.version||'')!==expectedVersion)throw failure('changed','El formulario local cambió durante la descarga.');
        const old=await driver.get(STORE,key+':candidate');
        const row={id:key+':candidate',kind:'candidate',userId,empresaId,visitaId:Number(id),scope,version:crypto.randomUUID(),templateVersion:context.offline_authorization.template_version,baseVersion:expectedVersion,context:clone(context),savedAt:now()};
        if(JSON.stringify(row).length*2>MAX_BYTES)throw failure('capacity','El formulario supera el espacio reservado para su preparación.');
        await publishRow(row,old?.version||'');
    };
    const prepared=async()=>{
        await storage();
        if(driver.visitaContextMetadata){const rows=await driver.visitaContextMetadata({userId,empresaId,scope});await active();return rows.filter(row=>row.kind==='context').map(row=>({...row,revision:row.expiresAt>now()?row.revision:null,candidate:rows.find(item=>item.kind==='candidate'&&item.visitaId===row.visitaId),hint:rows.find(item=>item.kind==='update_hint'&&item.visitaId===row.visitaId)}));}
        const rows=await driver.getAll(STORE,null,{userId});await active();
        const owned=rows.filter(row=>row?.userId===userId&&row.empresaId===empresaId&&row.scope===scope);
        const metadata=row=>row?{visitaId:row.visitaId,revision:row.remote_revision||formRevision(row.context),version:row.version,baseVersion:row.baseVersion,responsesVersion:Number(row.context?.visita?.responses_version||0),mergePilot:Boolean(row.context?.visita?.offline_merge_pilot)}:null;
        return owned.filter(row=>row.kind==='context').map(row=>({...metadata(row),revision:rowValid(row,row.visitaId)?formRevision(row.context):null,candidate:metadata(owned.find(item=>item.kind==='candidate'&&item.visitaId===row.visitaId)),hint:metadata(owned.find(item=>item.kind==='update_hint'&&item.visitaId===row.visitaId))}));
    };
    const forgetCandidate=async(id,version)=>{await storage();await driver.compareAndDelete(STORE,visitaOfflineContextKey(user,id,scope)+':candidate',row=>row?.version===version&&row.scope===scope);};
    const noteRevision=async(id,revision)=>{
        if(!/^[a-f0-9]{64}$/.test(revision))throw failure('invalid_response','Revisión de formulario inválida.');
        await storage();const key=visitaOfflineContextKey(user,id,scope)+':hint',old=await driver.get(STORE,key);
        if(old?.remote_revision===revision)return;
        await publishRow({id:key,kind:'update_hint',userId,empresaId,visitaId:Number(id),scope,version:crypto.randomUUID(),templateVersion:'pending',remote_revision:revision,savedAt:now()},old?.version||'');
    };
    const forgetHint=async(id,version)=>{await storage();await driver.compareAndDelete(STORE,visitaOfflineContextKey(user,id,scope)+':hint',row=>row?.version===version&&row.scope===scope);};
    const load=id=>{
        if(!Number.isSafeInteger(Number(id))||Number(id)<1)return Promise.reject(failure('invalid_visit','Visita inválida.'));
        const key=visitaOfflineContextKey(user,id,scope);
        if(jobs.has(key)){const running=jobs.get(key);return running.client===client?running.promise:running.promise.catch(()=>{}).then(()=>load(id));}
        const job=(async()=>{
            const session=await active();
            if(!await isOnline()){
                if(await candidate(id)){const result=await promote(id);if(result?.context)return result;}
                const context=await read(id);
                if(context){const hint=await driver.get(STORE,key+':hint');return hint?.scope===scope?{context,source:'saved',reason:'form_update_pending',pendingReason:'download_failed'}:{context,source:'saved'};}
                throw failure('not_prepared','Abrí esta visita con conexión para preparar su formulario.');
            }
            let storageError=null,expectedVersion='';
            try{await storage();await migrate();await active();expectedVersion=(await driver.get(STORE,key))?.version||'';}catch(error){if(['canceled','forbidden'].includes(error.code))throw error;storageError=error;}
            let context;
            try{context=await fetchContext(id,session);}
            catch(error){
                const status=Number(error?.response?.status??error?.status);
                if([403,404].includes(status)){await revoke(id).catch(()=>{});throw error;}
                if(!status||status===401||status===408||status===429||status>=500){const cached=await read(id).catch(()=>null);if(cached)return {context:cached,source:'saved',reason:status===401?'session':'connection'};}
                throw error;
            }
            await active();validate(context,id);
            let formChanged=false;
            if(!storageError)try{
                const previous=await driver.get(STORE,key);
                // Una resolución C6 puede conservar la estructura y cambiar respuestas.
                // Debe respetar el mismo journal y las mismas barreras del editor.
                const pilotChanged=Boolean(previous?.context?.visita?.offline_merge_pilot||context.visita?.offline_merge_pilot)
                    && (Number(previous?.context?.visita?.responses_version||0)!==Number(context.visita?.responses_version||0)
                        ||previous?.context?.visita?.offline_merge_snapshot_hash!==context.visita?.offline_merge_snapshot_hash);
                if(formRevision(previous?.context)!==formRevision(context)||pilotChanged){
                    formChanged=true;
                    await stage(id,context,expectedVersion);
                    const applied=await promote(id);
                    if(applied?.context)return applied;
                    if(applied?.reason==='form_update_pending')throw failure('pending_template','Hay una actualización del formulario pendiente de conciliación. Se conserva el trabajo local.');
                }
                context=await publish(id,context,expectedVersion);
                await clearAppliedHint(id,formRevision(context));
            }catch(error){
                if(['canceled','forbidden'].includes(error.code))throw error;
                if(error.code==='pending_template')throw error;
                if(error.code==='changed'){const cached=await read(id);if(cached)return {context:cached,source:'saved',reason:'concurrent'};}
                if(formChanged){const cached=await read(id).catch(()=>null);if(cached)return {context:cached,source:'saved',reason:'form_update_pending',pendingReason:'download_failed',storageError:String(error.message)};throw error;}
                storageError=error;
            }
            return {context,source:'server',storageError:storageError?String(storageError.message):null};
        })().finally(()=>{if(jobs.get(key)?.promise===job)jobs.delete(key);});
        jobs.set(key,{client,promise:job});return job;
    };
    const saveSignature=async(id,data,clienteLegajoId='')=>{
        await storage();await active();const key=visitaOfflineContextKey(user,id,scope);
        return driver.mutate(STORE,key,row=>{
            if(disposed||!isCurrent()||!rowValid(row,id))return null;
            const next={...row,version:crypto.randomUUID(),context:{...row.context,firma_cierre:{...clone(data),user_id:userId,cliente_legajo_id:String(clienteLegajoId)}}};
            if(JSON.stringify(next).length*2>MAX_BYTES)throw failure('capacity','La configuración de firma supera el espacio disponible.');
            return next;
        });
    };
    const signature=async(id,clienteLegajoId='')=>{
        const context=await read(id),preview=context?.firma_cierre;
        if(Number(preview?.user_id)===userId&&String(preview.cliente_legajo_id||'')===String(clienteLegajoId))return preview;
        const policy=context?.visita?.firmas_config;
        // El contexto autorizado ya confirma el circuito histórico. Conserva la
        // exigencia de dibujar/aclarar la firma; no autoriza un circuito activo.
        if(!clienteLegajoId&&policy?.activa===false&&/^[a-f0-9]{64}$/.test(policy.version||''))return {activa:false};
        if(policy?.activa===true&&/^[a-f0-9]{64}$/.test(policy.version||'')&&['sin_firma','firma_sola','firma_comprobante'].includes(policy.cierre)){
            const clientesAutorizados=Array.isArray(context?.visita?.clientes_autorizados_firma)?context.visita.clientes_autorizados_firma:[];
            const legajoSeleccionado=clienteLegajoId?clientesAutorizados.find(l=>String(l.id)===String(clienteLegajoId)):null;
            if(clienteLegajoId&&!legajoSeleccionado)return null;
            const requeridos={};
            if(policy.cierre!=='sin_firma'){
                for(const slot of (policy.visita_firmantes||[])){
                    requeridos[slot]={documentos:['informe_visita'],user_id:slot==='tecnico'?userId:null};
                }
            }
            if(policy.cierre==='firma_comprobante'){
                for(const slot of (policy.comprobante_firmantes||[])){
                    if(!requeridos[slot])requeridos[slot]={documentos:[],user_id:slot==='tecnico'?userId:null};
                    requeridos[slot].documentos.push('comprobante_servicio');
                }
            }
            if(legajoSeleccionado&&requeridos.cliente){
                requeridos.cliente.legajo_id=legajoSeleccionado.id;
            }
            const firmados=[];
            if(legajoSeleccionado&&requeridos.cliente&&Array.isArray(legajoSeleccionado.alcance)&&requeridos.cliente.documentos.every(d=>legajoSeleccionado.alcance.includes(d))&&legajoSeleccionado.autorizado_automatico){
                firmados.push('cliente');
            }
            return {
                activa:true,
                offline_synthesized:true,
                clientes_autorizados:clientesAutorizados,
                expediente:{
                    id:0,
                    estado:Object.keys(requeridos).every(s=>firmados.includes(s))?'completo':'pendiente',
                    c5:false,
                    es_revision_vigente:true,
                    tipo:'cierre_visita',
                    visita_id:Number(context.visita.id),
                    politica_version:policy.version,
                    contenido_hash:'0'.repeat(64),
                    offline_synthesized:true,
                    permitir_diferir_cliente:Boolean(policy.cliente_permitir_diferir_cierre??true),
                    confirmacion_usuarios:policy.uso_firma_usuarios==='confirmar',
                    requeridos,
                    firmados,
                    pendientes:Object.keys(requeridos).filter(s=>!firmados.includes(s)),
                    guardadas:{},
                    contenido:{observacion:context.visita.observacion||''},
                },
            };
        }
        return null;
    };
    return {scope,load,read,revoke,candidate,promote,prepared,forgetCandidate,noteRevision,forgetHint,saveSignature,signature,dispose(){disposed=true;}};
}

// El ACK modifica únicamente el snapshot operativo ya preparado de su propietario.
// No amplía permisos, vigencia ni la autorización de la plantilla.
export async function acknowledgeVisitaContext(entry,response,{driver=getOfflineStorageDriver()}={}){
    await driver.init();
    if(isNativePlatform()&&driver.effectiveEngine!=='sqlite')throw failure('storage','No se pudo actualizar el formulario en SQLite.');
    const rows=await driver.getAll(STORE,null,{userId:Number(entry.userId),contexts:{visitaId:Number(entry.visitaId)}});
    for(const row of rows){
        if(row?.kind!=='context'||row.userId!==Number(entry.userId)||row.visitaId!==Number(entry.visitaId))continue;
        await driver.mutate(STORE,row.id,current=>current?.version===row.version&&Number(response.responses_version??current.context.visita.responses_version)>=Number(current.context.visita.responses_version||0)?{...current,version:crypto.randomUUID(),context:{...current.context,visita:{...current.context.visita,...cloneVisitaSnapshot(entry.payload),responses_version:response.responses_version??current.context.visita.responses_version}}}:null);
    }
}
